import { ModalWrapper } from '@shared/ui/modal-wrapper';
import stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { Button, Input, PreloaderCircular } from '@shared/ui/kit';
import { FC, useEffect, useMemo, useState } from 'react';
import { phoneNumberValidator } from '@shared/libs/phone-validator';
import { phraseValidator, REQURED_PHRASE_MESSAGE } from '@pages/event/lib/validator';
import { IOrganizer } from '@features/organizer/model/types';
import { SuccessBlock } from '@shared/ui/success-modal';
import { ErrorBlock } from '@shared/ui/error-modal';
import { useUpdateOrganizerMutation } from '@pages/event/api/useUpdateOrganizerMutation';
import { typography } from '@shared/tokens/typography.stylex';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { XmarkIcon } from '@shared/assets/icons';

type ModalStep = 'edit' | 'success' | 'error';

interface EditOrganizerModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizer?: IOrganizer;
}

export const EditOrganizerModal: FC<EditOrganizerModalProps> = ({ isOpen, onClose, organizer }) => {
  const [lastName, setLastName] = useState(organizer?.lastName || '');
  const [firstName, setFirstName] = useState(organizer?.firstName || '');
  const [phone, setPhone] = useState(organizer?.phone || '');

  const [lastNameErrors, setLastNameErrors] = useState<string | false>(false);
  const [firstNameErrors, setFirstNameErrors] = useState<string | false>(false);
  const [phoneErrors, setPhoneErrors] = useState<string | false>(false);
  const [duplicatePhoneError, setDuplicatePhoneError] = useState(false);

  const [currentStep, setCurrentStep] = useState<ModalStep>('edit');

  const [{ fetching }, updateOrganizer] = useUpdateOrganizerMutation();

  useEffect(() => {
    if (!lastName.trim()) {
      setLastNameErrors('Поле фамилия не может быть пустым');
    } else {
      phraseValidator(lastName).then(setLastNameErrors);
    }
  }, [lastName]);

  useEffect(() => {
    if (!firstName.trim()) {
      setFirstNameErrors('Поле имя не может быть пустым');
    } else {
      phraseValidator(firstName).then(setFirstNameErrors);
    }
  }, [firstName]);

  useEffect(() => {
    if (!phone.trim()) {
      setPhoneErrors('Обязательное поле');
    } else if (phone.length < 17) {
      setPhoneErrors(`${REQURED_PHRASE_MESSAGE}`);
    } else {
      setPhoneErrors(false);
    }
  }, [phone]);

  const isSaveDisabled = useMemo(() => {
    return (
      !!lastNameErrors ||
      !!firstNameErrors ||
      !!phoneErrors ||
      duplicatePhoneError ||
      !lastName ||
      !firstName ||
      !phone ||
      fetching
    );
  }, [lastNameErrors, firstNameErrors, phoneErrors, duplicatePhoneError, lastName, firstName, phone, fetching]);

  useEffect(() => {
    if (isOpen && organizer) {
      setLastName(organizer.lastName);
      setFirstName(organizer.firstName);
      // Организатор мог быть заведён без телефона — тогда поле открывается пустым
      setPhone(phoneNumberValidator(organizer.phone ?? ''));
      setCurrentStep('edit');
      setDuplicatePhoneError(false);
    } else {
      setLastName('');
      setFirstName('');
      setPhone('');
    }

    setLastNameErrors(false);
    setFirstNameErrors(false);
    setPhoneErrors(false);
  }, [isOpen, organizer]);

  const handleChangeNumber = (value: string) => {
    const formattedNumber = phoneNumberValidator(value);
    setPhone(formattedNumber);
    setDuplicatePhoneError(false);
  };

  const handleUpdateOrganizer = async () => {
    try {
      setDuplicatePhoneError(false);
      if (!organizer || organizer.id === undefined) {
        throw new Error('Организатор не выбран');
      }

      const result = await updateOrganizer({
        userId: organizer.id,
        input: { firstName, lastName, phone },
      });

      if (result.error) {
        const isDuplicateError = result.error.message.includes(
          'duplicate key value violates unique constraint "idx_users_phone"'
        );

        if (isDuplicateError) {
          setDuplicatePhoneError(true);
          return;
        }
      }

      setCurrentStep('success');
    } catch {
      setCurrentStep('error');
    }
  };

  const handleCloseModal = () => {
    setCurrentStep('edit');
    onClose();
  };

  const renderContent = () => {
    switch (currentStep) {
      case 'edit':
        return (
          <>
            <div {...stylex.props(styles.container)}>
              <div {...stylex.props(styles.headerContainer)}>
                <p {...stylex.props(styles.header)}>Изменить данные организатора</p>

                <UnstyledButton onClick={onClose}>
                  <XmarkIcon size="24" {...stylex.props(styles.xMarkIcon)} />
                </UnstyledButton>
              </div>

              <div {...stylex.props(styles.fields)}>
                <Input
                  placeholder="Введите фамилию"
                  inputMessage={lastNameErrors || ''}
                  status={lastNameErrors ? 'error' : undefined}
                  settings={{ width: 'auto', height: 'auto' }}
                  value={lastName}
                  changeFunction={setLastName}
                  labelText="Фамилия *"
                />

                <Input
                  placeholder="Введите имя"
                  inputMessage={firstNameErrors || ''}
                  status={firstNameErrors ? 'error' : undefined}
                  settings={{ width: 'auto', height: 'auto' }}
                  value={firstName}
                  changeFunction={setFirstName}
                  labelText="Имя *"
                />

                <Input
                  placeholder="Введите телефон"
                  value={phone}
                  inputMessage={duplicatePhoneError ? 'Пользователь с таким номером уже существует' : phoneErrors || ''}
                  status={phoneErrors || duplicatePhoneError ? 'error' : undefined}
                  settings={{ width: 'auto', height: 'auto' }}
                  changeFunction={handleChangeNumber}
                  labelText="Телефон *"
                />
              </div>
            </div>

            <div {...stylex.props(styles.actions)}>
              <Button
                onClick={onClose}
                variant="secondary"
                size="medium"
                settings={{ height: '36px', width: '85px', fontSize: typography['fontSizeCaption-1'] }}
              >
                Отмена
              </Button>

              <Button
                onClick={handleUpdateOrganizer}
                variant="accent"
                size="medium"
                settings={{ height: '36px', width: '103px', fontSize: typography['fontSizeCaption-1'] }}
                isDisabled={isSaveDisabled}
              >
                Сохранить
              </Button>
            </div>
          </>
        );

      case 'success':
        return (
          <SuccessBlock
            title="Изменить данные организатора"
            description="Данные организатора успешно изменены"
            onClose={handleCloseModal}
          />
        );

      case 'error':
        return (
          <ErrorBlock
            description="При изменении данных организатора произошла непредвиденная ошибка. Повторите попытку позднее"
            onClose={handleCloseModal}
          />
        );
    }
  };

  return (
    <ModalWrapper
      active={isOpen}
      settingsHeader={{ height: '16px', padding: '0px' }}
      settingsFooter={{ height: '16px', padding: '0px' }}
      settingsContainer={{ height: 'auto', width: '480px', padding: '0 24px' }}
    >
      {fetching ? (
        <div {...stylex.props(styles.loaderContainer)}>
          <PreloaderCircular variant="secondary" size="large" />
        </div>
      ) : (
        renderContent()
      )}
    </ModalWrapper>
  );
};
